const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('userToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Format time from "09:00:00" to "09:00"
const formatTimeForInput = (t) => (t ? String(t).substring(0, 5) : '');

// Map DB row to form format
const mapItemFromDb = (row) => ({
  id: row.id,
  title: row.title,
  description: row.description || '',
  day: String(row.day),
  startTime: formatTimeForInput(row.start_time),
  endTime: formatTimeForInput(row.end_time),
  location: row.location,
  speaker: row.speaker || '',
  type: row.type || 'session',
  capacity: row.capacity ? String(row.capacity) : '',
  status: row.status || 'active',
});

export const agendaService = {
  // Public - get agenda items for /agenda page
  getItems: async (day) => {
    const url = `${API_BASE_URL}/agenda/items${day ? `?day=${day}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch agenda');
    const json = await res.json();
    return json.success ? json.data : [];
  },

  // Public - get agenda settings
  getSettings: async () => {
    const res = await fetch(`${API_BASE_URL}/agenda/settings`);
    if (!res.ok) throw new Error('Failed to fetch settings');
    const json = await res.json();
    return json.success ? json.data : {};
  },

  // Public - get agenda images
  getImages: async () => {
    const res = await fetch(`${API_BASE_URL}/agenda/images`);
    if (!res.ok) throw new Error('Failed to fetch images');
    const json = await res.json();
    return json.success ? json.data : [];
  },

  // Admin - update settings
  updateSettings: async (data) => {
    const res = await fetch(`${API_BASE_URL}/agenda/admin/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Failed to update');
    return json;
  },

  // Admin - upload image for agenda (returns path to use in updateImage)
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';
    const token = localStorage.getItem('adminToken') || localStorage.getItem('userToken');
    const res = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.error || json.message || 'Upload failed');
    return json.fileUrl || json.s3Location;
  },

  // Admin - update image path
  updateImage: async (id, imagePath, imageAlt) => {
    const res = await fetch(`${API_BASE_URL}/agenda/admin/images/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ image_path: imagePath, image_alt: imageAlt }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Failed to update');
    return json;
  },

  // Admin - get all items (including inactive)
  getAllItems: async () => {
    const res = await fetch(`${API_BASE_URL}/agenda/admin/items`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch agenda items');
    const json = await res.json();
    return json.success ? json.data.map(mapItemFromDb) : [];
  },

  // Admin - create item
  createItem: async (data) => {
    const res = await fetch(`${API_BASE_URL}/agenda/admin/items`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        title: data.title,
        description: data.description,
        day: data.day,
        startTime: data.startTime,
        endTime: data.endTime,
        location: data.location,
        speaker: data.speaker,
        type: data.type,
        capacity: data.capacity ? parseInt(data.capacity, 10) : 0,
        status: data.status,
      }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Failed to create');
    return json;
  },

  // Admin - update item
  updateItem: async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/agenda/admin/items/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        title: data.title,
        description: data.description,
        day: data.day,
        startTime: data.startTime,
        endTime: data.endTime,
        location: data.location,
        speaker: data.speaker,
        type: data.type,
        capacity: data.capacity ? parseInt(data.capacity, 10) : 0,
        status: data.status,
      }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Failed to update');
    return json;
  },

  // Admin - delete item
  deleteItem: async (id) => {
    const res = await fetch(`${API_BASE_URL}/agenda/admin/items/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Failed to delete');
    return json;
  },
};

export default agendaService;
